import * as React from 'react';
import { useFormik } from 'formik';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    TextareaAutosize,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ScheduleEntry } from '../../../domain/ScheduleEntry';
import { Assignment } from '../../../domain/Publisher';
import PublisherTextField from '../schedule-table/PublisherTextField';

interface Props {
    isOpen: boolean;
    entry?: ScheduleEntry;
    assignments: Assignment[];
    onSubmit: (entry: ScheduleEntry) => void;
    onDelete: (entry: ScheduleEntry) => void;
    onClose: () => void;
}

const participantsOptions = [
    'elders',
    'appointed',
    'brothers',
    'sisters',
    'all'
];

const DesktopScheduleEntryDialog = (props: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [speakerFromCongregation, setSpeakerFromCongregation] = React.useState<boolean>(props.entry ? !props.entry.customSpeaker : true);
    const [isImpromptu, setIsImpromptu] = React.useState<boolean>(props.entry ? !!props.entry.impromptuId : false);

    React.useEffect(() => {
        if (props.entry) {
            setSpeakerFromCongregation(!props.entry.customSpeaker);
            setIsImpromptu(!!props.entry.impromptuId);
        } else {
            setSpeakerFromCongregation(true);
            setIsImpromptu(false);
        }
    }, [props.entry]);

    const schema = yup.object().shape({
        name: yup.string().required(t('field_is_required', { name: t('name') })),
    });

    const formik = useFormik<ScheduleEntry>({
        validationSchema: schema,
        enableReinitialize: true,
        initialValues: props.entry || {} as ScheduleEntry,
        onSubmit: (entry) => {
            props.onSubmit(entry);
            props.onClose();
        }
    });

    const handleDelete = () => {
        if (!props.entry) {
            return;
        }
        props.onDelete(props.entry);
        props.onClose();
    }

    const { values } = formik;

    const assignmentValue = React.useMemo(() => {
        const assignmentId = values.assignment?.id ?? values.assignmentId;
        if (!assignmentId) {
            return values.assignment ?? null;
        }

        return props.assignments.find((assignment) => assignment.id === assignmentId) ?? values.assignment ?? null;
    }, [props.assignments, values.assignment, values.assignmentId]);

    return (
        <Dialog maxWidth="md" fullWidth open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t('schedule_entry')}</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={8}>
                            <TextField
                                margin="normal"
                                id="name"
                                label={t('name')}
                                fullWidth
                                variant="standard"
                                value={values.name || ''}
                                onChange={formik.handleChange}
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                margin="normal"
                                id="position"
                                label={t('position')}
                                type="number"
                                fullWidth
                                variant="standard"
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                value={values.position || ''}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextField
                                margin="normal"
                                id="room"
                                label={t('room')}
                                type="number"
                                fullWidth
                                variant="standard"
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                value={values.room || ''}
                                onChange={formik.handleChange}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Autocomplete
                                size="small"
                                disablePortal
                                id="participants"
                                options={participantsOptions}
                                getOptionLabel={(option: string) => t(option)}
                                value={values.participants || null}
                                renderInput={(params) => <TextField {...params} margin="normal" label={t('participants')} />}
                                onChange={(e, option) => {
                                    formik.setFieldValue('participants', option);
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete<Assignment>
                                size="small"
                                disablePortal
                                id="assignment"
                                options={props.assignments}
                                getOptionLabel={(option) => t(option.name)}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={assignmentValue}
                                renderInput={(params) => <TextField {...params} margin="normal" label={t('assignment')} />}
                                onChange={(e, option) => {
                                    formik.setFieldValue('assignment', option || undefined);
                                    formik.setFieldValue('assignmentId', option?.id);
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={speakerFromCongregation}
                                        onChange={(event) => setSpeakerFromCongregation(event.target.checked)}
                                    />
                                }
                                label={t('speaker_from_a_congregation')}
                            />
                        </FormControl>

                        {speakerFromCongregation ? (
                            props.entry && (
                                <PublisherTextField
                                    entry={props.entry}
                                    field="speaker"
                                    editable={true}
                                    assigned={[]}
                                    onEntryUpdate={(entry) => {
                                        formik.setFieldValue('speakerId', entry.speaker?.id);
                                        formik.setFieldValue('speaker', entry.speaker);
                                    }}
                                />
                            )
                        ) : (
                            <TextField
                                margin="normal"
                                id="customSpeaker"
                                label={t('speaker')}
                                fullWidth
                                variant="standard"
                                value={values.customSpeaker || ''}
                                onChange={formik.handleChange}
                            />
                        )}

                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(values.companionNeeded)}
                                        onChange={(event) => formik.setFieldValue('companionNeeded', event.target.checked)}
                                    />
                                }
                                label={t('companion')}
                            />
                        </FormGroup>

                        {values.companionNeeded && props.entry && (
                            <PublisherTextField
                                entry={props.entry}
                                field="companion"
                                editable={true}
                                assigned={[]}
                                onEntryUpdate={(entry) => {
                                    formik.setFieldValue('companion', entry.companion);
                                    formik.setFieldValue('companionId', entry.companion?.id);
                                }}
                            />
                        )}

                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isImpromptu}
                                        onChange={(event) => setIsImpromptu(event.target.checked)}
                                    />
                                }
                                label={t('impromptu')}
                            />
                        </FormGroup>

                        {isImpromptu && props.entry && (
                            <PublisherTextField
                                entry={props.entry}
                                field="impromptu"
                                editable={true}
                                assigned={[]}
                                onEntryUpdate={(entry) => {
                                    formik.setFieldValue('impromptuId', entry.impromptuId);
                                    formik.setFieldValue('impromptu', entry.impromptu);
                                }}
                            />
                        )}

                        <Box>
                            <TextareaAutosize
                                id="notes"
                                value={values.notes || ''}
                                minRows={4}
                                placeholder={t('notes')}
                                style={{ width: '100%', padding: 8, fontFamily: theme.typography.fontFamily }}
                                onChange={formik.handleChange}
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {props.entry && (
                        <Button color="error" onClick={handleDelete}>
                            {t('delete')}
                        </Button>
                    )}
                    <Button onClick={props.onClose}>{t('cancel')}</Button>
                    <Button type="submit">{t('submit')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default DesktopScheduleEntryDialog;

